/**
 * Fase 9 — faturamento do SaaS: planos, limites, estados da assinatura, modo leitura e métricas
 * do painel do dono (MRR/ARR/churn). Tudo aqui é puro e testável; o backend aplica os limites.
 */
import { z } from "zod";

// ---- Planos, limites e recursos ----------------------------------------------------------------

export const LIMIT_KEYS = [
  "users",
  "adAccounts",
  "clients",
  "aiCredits",
  "activeRules",
  "reportLinks",
  "leadDestinations",
] as const;
export type LimitKey = (typeof LIMIT_KEYS)[number];

/** `null` = ilimitado. */
export type PlanLimits = Record<LimitKey, number | null>;

export const LIMIT_LABELS: Record<LimitKey, string> = {
  users: "Usuários",
  adAccounts: "Contas de anúncio conectadas",
  clients: "Clientes",
  aiCredits: "Créditos de IA por mês",
  activeRules: "Regras ativas",
  reportLinks: "Links de relatório",
  leadDestinations: "Destinos de leads",
};

/** Forma no singular/plural para as mensagens de limite. */
const LIMIT_NOUNS: Record<LimitKey, [string, string]> = {
  users: ["usuário", "usuários"],
  adAccounts: ["conta de anúncio conectada", "contas de anúncio conectadas"],
  clients: ["cliente", "clientes"],
  aiCredits: ["crédito de IA por mês", "créditos de IA por mês"],
  activeRules: ["regra ativa", "regras ativas"],
  reportLinks: ["link de relatório ativo", "links de relatório ativos"],
  leadDestinations: ["destino de leads", "destinos de leads"],
};

export const FEATURE_KEYS = ["whiteLabel", "customDomain", "conversionsApi", "aiExtended"] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];
export type PlanFeatures = Record<FeatureKey, boolean>;

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  whiteLabel: "Relatórios white-label (logo e cor da agência)",
  customDomain: "Domínio próprio para relatórios",
  conversionsApi: "Conversions API",
  aiExtended: "IA ampliada",
};

export const BILLING_CYCLES = ["mensal", "anual"] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];
export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = { mensal: "Mensal", anual: "Anual" };

export const SUBSCRIPTION_STATUSES = [
  "trialing",
  "active",
  "past_due",
  "canceled",
  "incomplete",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trialing: "Em teste",
  active: "Ativa",
  past_due: "Pagamento pendente",
  canceled: "Cancelada",
  incomplete: "Aguardando pagamento",
};

export const BILLING_MODES = ["mock", "live"] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

/** Ambientes que podem ter ids de Price do Stripe próprios. */
export const BILLING_ENVIRONMENTS = ["development", "staging", "production"] as const;
export type BillingEnvironment = (typeof BILLING_ENVIRONMENTS)[number];
export type StripePriceIds = Partial<
  Record<BillingEnvironment, { monthly: string | null; yearly: string | null }>
>;

export const TRIAL_DAYS = 7;
export const PAST_DUE_GRACE_DAYS = 3;
export const TRIAL_ENDING_NOTICE_DAYS = 2;

/** Seed dos planos da especificação (os valores são editáveis no painel do dono). */
export const DEFAULT_PLANS: readonly {
  code: string;
  name: string;
  description: string;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  limits: PlanLimits;
  features: PlanFeatures;
  sortOrder: number;
}[] = [
  {
    code: "starter",
    name: "Starter",
    description: "Para o gestor que está começando: 1 usuário e até 5 contas de anúncio.",
    priceMonthlyCents: 9700,
    priceYearlyCents: 97000,
    limits: {
      users: 1,
      adAccounts: 5,
      clients: 5,
      aiCredits: 200,
      activeRules: 0,
      reportLinks: 3,
      leadDestinations: 2,
    },
    features: { whiteLabel: false, customDomain: false, conversionsApi: false, aiExtended: false },
    sortOrder: 1,
  },
  {
    code: "pro",
    name: "Pro",
    description: "Para agências em crescimento: 5 usuários, 25 contas, regras e relatórios white-label.",
    priceMonthlyCents: 29700,
    priceYearlyCents: 297000,
    limits: {
      users: 5,
      adAccounts: 25,
      clients: 25,
      aiCredits: 1000,
      activeRules: 50,
      reportLinks: 50,
      leadDestinations: 20,
    },
    features: { whiteLabel: true, customDomain: false, conversionsApi: false, aiExtended: false },
    sortOrder: 2,
  },
  {
    code: "agency",
    name: "Agency",
    description: "Para agências grandes: usuários e contas ilimitados, IA ampliada, CAPI e domínio próprio.",
    priceMonthlyCents: 69700,
    priceYearlyCents: 697000,
    limits: {
      users: null,
      adAccounts: null,
      clients: null,
      aiCredits: 5000,
      activeRules: null,
      reportLinks: null,
      leadDestinations: null,
    },
    features: { whiteLabel: true, customDomain: true, conversionsApi: true, aiExtended: true },
    sortOrder: 3,
  },
];

/** Verdadeiro quando `used + adding` cabe no limite (`null` = ilimitado). */
export function withinLimit(limit: number | null, used: number, adding = 1): boolean {
  return limit === null || used + adding <= limit;
}

export function formatLimit(limit: number | null): string {
  return limit === null ? "Ilimitado" : new Intl.NumberFormat("pt-BR").format(limit);
}

/** Mensagem de bloqueio por limite, em pt-BR, com o CTA de upgrade. */
export function planLimitMessage(key: LimitKey, limit: number, planName: string): string {
  const [one, many] = LIMIT_NOUNS[key];
  const noun = limit === 1 ? one : many;
  const amount = limit === 0 ? `não inclui ${many}` : `permite até ${limit} ${noun}`;
  return `O plano ${planName} ${amount}. Faça upgrade do plano para continuar.`;
}

export function planFeatureMessage(feature: FeatureKey, planName: string): string {
  return `O plano ${planName} não inclui ${FEATURE_LABELS[feature].toLowerCase()}. Faça upgrade do plano para usar.`;
}

// ---- Acesso (trial, inadimplência, modo leitura) -----------------------------------------------

export const READ_ONLY_REASONS = ["trial_expirado", "inadimplente", "cancelado", "suspenso", "incompleto"] as const;
export type ReadOnlyReason = (typeof READ_ONLY_REASONS)[number];

export const READ_ONLY_MESSAGES: Record<ReadOnlyReason, string> = {
  trial_expirado:
    "O teste grátis acabou. O workspace está em modo leitura: você vê e exporta tudo, mas não cria nem altera nada até assinar um plano.",
  inadimplente:
    "O pagamento da assinatura está pendente há mais de 3 dias. O workspace está em modo leitura até a cobrança ser regularizada.",
  cancelado:
    "A assinatura foi cancelada. O workspace está em modo leitura: assine de novo para voltar a criar e alterar.",
  suspenso: "Este workspace foi suspenso pelo suporte do IAGENIX Ads. Fale com o suporte para reativar.",
  incompleto:
    "O primeiro pagamento ainda não foi confirmado (o boleto leva até 1 dia útil). Enquanto isso o workspace fica em modo leitura.",
};

export type AccessState = "trial" | "ativo" | "tolerancia" | "modo_leitura";

export interface AccessInput {
  status: SubscriptionStatus;
  trialEndsAt: Date | null;
  pastDueSince: Date | null;
  currentPeriodEnd: Date | null;
  suspendedAt: Date | null;
}

export interface AccessResult {
  state: AccessState;
  readOnly: boolean;
  reason: ReadOnlyReason | null;
  /** Dias inteiros que faltam no teste (arredondado para cima), ou null fora do teste. */
  trialDaysLeft: number | null;
  /** Quando a tolerância do pagamento pendente termina. */
  graceEndsAt: Date | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function trialAccess(trialEndsAt: Date | null, now: Date): AccessResult | null {
  if (!trialEndsAt || trialEndsAt.getTime() <= now.getTime()) return null;
  return {
    state: "trial",
    readOnly: false,
    reason: null,
    trialDaysLeft: Math.max(1, Math.ceil((trialEndsAt.getTime() - now.getTime()) / DAY_MS)),
    graceEndsAt: null,
  };
}

function readOnly(reason: ReadOnlyReason): AccessResult {
  return { state: "modo_leitura", readOnly: true, reason, trialDaysLeft: null, graceEndsAt: null };
}

/**
 * Decide se o workspace pode escrever. Bloqueio suave: modo leitura quando o teste acabou sem
 * assinatura, quando o pagamento está pendente além da tolerância, quando a assinatura foi
 * cancelada (e o período pago acabou) ou quando o dono do SaaS suspendeu o workspace.
 */
export function computeAccess(
  input: AccessInput,
  now: Date = new Date(),
  graceDays: number = PAST_DUE_GRACE_DAYS,
): AccessResult {
  if (input.suspendedAt) return readOnly("suspenso");
  switch (input.status) {
    case "active":
      return { state: "ativo", readOnly: false, reason: null, trialDaysLeft: null, graceEndsAt: null };
    case "trialing":
      return trialAccess(input.trialEndsAt, now) ?? readOnly("trial_expirado");
    case "incomplete":
      // Boleto aguardando compensação: o teste que ainda vale continua valendo.
      return trialAccess(input.trialEndsAt, now) ?? readOnly("incompleto");
    case "past_due": {
      const since = input.pastDueSince ?? now;
      const graceEndsAt = new Date(since.getTime() + graceDays * DAY_MS);
      if (now.getTime() < graceEndsAt.getTime()) {
        return { state: "tolerancia", readOnly: false, reason: null, trialDaysLeft: null, graceEndsAt };
      }
      return { ...readOnly("inadimplente"), graceEndsAt };
    }
    case "canceled":
      if (input.currentPeriodEnd && input.currentPeriodEnd.getTime() > now.getTime()) {
        return { state: "ativo", readOnly: false, reason: null, trialDaysLeft: null, graceEndsAt: null };
      }
      return readOnly("cancelado");
  }
}

// ---- Máquina de estados da assinatura ----------------------------------------------------------

/** Status do Stripe → status interno. */
export function mapStripeStatus(stripeStatus: string): SubscriptionStatus {
  switch (stripeStatus) {
    case "active":
      return "active";
    case "trialing":
      return "trialing";
    case "past_due":
    case "unpaid":
    case "paused":
      return "past_due";
    case "canceled":
    case "incomplete_expired":
      return "canceled";
    default:
      return "incomplete";
  }
}

export type SubscriptionEvent =
  | { type: "checkout_concluido"; paid: boolean }
  | { type: "assinatura_atualizada"; stripeStatus: string }
  | { type: "pagamento_confirmado" }
  | { type: "pagamento_falhou" }
  | { type: "acao_necessaria" }
  | { type: "assinatura_cancelada" };

export interface SubscriptionState {
  status: SubscriptionStatus;
  pastDueSince: Date | null;
}

/**
 * Transições da assinatura a partir dos eventos do Stripe. `pastDueSince` marca a entrada em
 * `past_due` (é o relógio da tolerância) e só é limpo quando o status sai de `past_due`.
 */
export function nextSubscriptionState(
  current: SubscriptionState,
  event: SubscriptionEvent,
  now: Date = new Date(),
): SubscriptionState {
  let status: SubscriptionStatus;
  switch (event.type) {
    case "checkout_concluido":
      status = event.paid ? "active" : current.status === "active" ? "active" : "incomplete";
      break;
    case "assinatura_atualizada":
      status = mapStripeStatus(event.stripeStatus);
      break;
    case "pagamento_confirmado":
      status = current.status === "canceled" ? "canceled" : "active";
      break;
    case "pagamento_falhou":
    case "acao_necessaria":
      // Falha no primeiro pagamento (sem assinatura ativa ainda) continua "aguardando pagamento".
      status =
        current.status === "canceled"
          ? "canceled"
          : current.status === "incomplete" || current.status === "trialing"
            ? current.status
            : "past_due";
      break;
    case "assinatura_cancelada":
      status = "canceled";
      break;
  }
  const pastDueSince =
    status === "past_due" ? (current.status === "past_due" && current.pastDueSince ? current.pastDueSince : now) : null;
  return { status, pastDueSince };
}

// ---- Métricas do dono (MRR, ARR, churn) ---------------------------------------------------------

export interface DiscountInfo {
  couponId: string | null;
  code: string | null;
  percentOff: number | null;
  amountOffCents: number | null;
  /** null = para sempre. */
  endsAt: string | null;
}

/** Valor mensal recorrente de uma assinatura em centavos (anual ÷ 12, com desconto vigente). */
export function monthlyRecurringCents(
  input: {
    cycle: BillingCycle;
    priceMonthlyCents: number;
    priceYearlyCents: number;
    discount: DiscountInfo | null;
  },
  now: Date = new Date(),
): number {
  let cents = input.cycle === "anual" ? input.priceYearlyCents / 12 : input.priceMonthlyCents;
  const d = input.discount;
  if (d && (!d.endsAt || new Date(d.endsAt).getTime() > now.getTime())) {
    if (d.percentOff) cents = cents * (1 - Math.min(100, d.percentOff) / 100);
    if (d.amountOffCents) {
      const monthlyOff = input.cycle === "anual" ? d.amountOffCents / 12 : d.amountOffCents;
      cents = cents - monthlyOff;
    }
  }
  return Math.max(0, Math.round(cents));
}

export interface MetricsSubscription {
  status: SubscriptionStatus;
  /** Valor mensal recorrente (já normalizado e com desconto). */
  mrrCents: number;
  /** Primeira vez que virou pagante (null = nunca pagou). */
  activatedAt: Date | null;
  canceledAt: Date | null;
  workspaceCreatedAt: Date;
}

export interface SaasMetrics {
  mrrCents: number;
  arrCents: number;
  payingCount: number;
  newThisMonth: number;
  canceledThisMonth: number;
  /** Cancelados no mês ÷ pagantes no início do mês (0–1). */
  churnRate: number;
  series: { month: string; mrrCents: number }[];
}

function monthStart(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function addMonths(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
}

function payingAt(s: MetricsSubscription, at: Date): boolean {
  if (!s.activatedAt || s.activatedAt.getTime() > at.getTime()) return false;
  return !s.canceledAt || s.canceledAt.getTime() > at.getTime();
}

/** Pagante agora: ativa ou com pagamento pendente (ainda cobrando). */
export function isPaying(status: SubscriptionStatus): boolean {
  return status === "active" || status === "past_due";
}

/**
 * MRR = soma do valor mensal das assinaturas pagantes (ativas ou pendentes); ARR = MRR × 12.
 * Série mensal: MRR no fim de cada mês, reconstruído pelas datas de ativação/cancelamento
 * (usa o preço atual de cada assinatura — aproximação documentada no README).
 */
export function computeSaasMetrics(
  subs: readonly MetricsSubscription[],
  now: Date = new Date(),
  months = 12,
): SaasMetrics {
  const paying = subs.filter((s) => isPaying(s.status));
  const mrrCents = paying.reduce((sum, s) => sum + s.mrrCents, 0);
  const start = monthStart(now);
  const newThisMonth = subs.filter((s) => s.workspaceCreatedAt.getTime() >= start.getTime()).length;
  const canceledThisMonth = subs.filter(
    (s) => s.activatedAt && s.canceledAt && s.canceledAt.getTime() >= start.getTime() && s.canceledAt.getTime() <= now.getTime(),
  ).length;
  const payingAtStart = subs.filter((s) => payingAt(s, start)).length;
  const churnRate = payingAtStart === 0 ? 0 : canceledThisMonth / payingAtStart;
  const series: { month: string; mrrCents: number }[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const m = addMonths(start, -i);
    const end = i === 0 ? now : new Date(addMonths(m, 1).getTime() - 1);
    const value = subs.filter((s) => payingAt(s, end)).reduce((sum, s) => sum + s.mrrCents, 0);
    series.push({ month: `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, "0")}`, mrrCents: value });
  }
  return { mrrCents, arrCents: mrrCents * 12, payingCount: paying.length, newThisMonth, canceledThisMonth, churnRate, series };
}

// ---- Schemas de entrada ------------------------------------------------------------------------

const limitValue = z.number().int().min(0).max(1_000_000).nullable();

export const planLimitsSchema = z.object({
  users: limitValue,
  adAccounts: limitValue,
  clients: limitValue,
  aiCredits: limitValue,
  activeRules: limitValue,
  reportLinks: limitValue,
  leadDestinations: limitValue,
});

export const planFeaturesSchema = z.object({
  whiteLabel: z.boolean(),
  customDomain: z.boolean(),
  conversionsApi: z.boolean(),
  aiExtended: z.boolean(),
});

const priceIdValue = z
  .string()
  .trim()
  .regex(/^price_[A-Za-z0-9]+$/, "Use um id de Price do Stripe (price_...).")
  .nullable();

export const updatePlanSchema = z.object({
  name: z.string().trim().min(2).max(60),
  description: z.string().trim().max(300).default(""),
  priceMonthlyCents: z.number().int().min(0).max(100_000_000),
  priceYearlyCents: z.number().int().min(0).max(1_000_000_000),
  limits: planLimitsSchema,
  features: planFeaturesSchema,
  active: z.boolean(),
  stripePriceIds: z
    .partialRecord(z.enum(BILLING_ENVIRONMENTS), z.object({ monthly: priceIdValue, yearly: priceIdValue }))
    .default({}),
});
export type UpdatePlanInput = z.input<typeof updatePlanSchema>;

export const checkoutSchema = z.object({
  planCode: z.string().trim().min(1).max(40),
  cycle: z.enum(BILLING_CYCLES).default("mensal"),
  coupon: z
    .string()
    .trim()
    .max(40)
    .transform((v) => v.toUpperCase())
    .optional(),
});
export type CheckoutInput = z.input<typeof checkoutSchema>;

export const createCouponSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(3)
      .max(40)
      .regex(/^[A-Za-z0-9-]+$/, "Use letras, números e hífen.")
      .transform((v) => v.toUpperCase()),
    name: z.string().trim().min(2).max(80),
    percentOff: z.number().int().min(1).max(100).nullable().default(null),
    amountOffCents: z.number().int().min(1).max(100_000_000).nullable().default(null),
    duration: z.enum(["once", "repeating", "forever"]).default("once"),
    durationInMonths: z.number().int().min(1).max(36).nullable().default(null),
    maxRedemptions: z.number().int().min(1).max(100_000).nullable().default(null),
  })
  .refine((v) => (v.percentOff === null) !== (v.amountOffCents === null), {
    message: "Informe porcentagem OU valor fixo de desconto.",
    path: ["percentOff"],
  })
  .refine((v) => v.duration !== "repeating" || v.durationInMonths !== null, {
    message: "Informe por quantos meses o desconto vale.",
    path: ["durationInMonths"],
  });
export type CreateCouponInput = z.input<typeof createCouponSchema>;

export const COUPON_DURATION_LABELS = { once: "Uma vez", repeating: "Por alguns meses", forever: "Para sempre" } as const;

export const extendTrialSchema = z.object({ days: z.number().int().min(1).max(90) });
export const discountSchema = z.object({ couponId: z.uuid() });
export const suspendSchema = z.object({ reason: z.string().trim().min(3).max(300) });
export const deleteWorkspaceSchema = z.object({ confirmName: z.string().trim().min(1) });
export const elevateSchema = z.object({ code: z.string().trim().regex(/^\d{6}$/, "Digite os 6 números do aplicativo.") });

export const MOCK_CHECKOUT_OUTCOMES = ["cartao", "boleto", "recusado"] as const;
export type MockCheckoutOutcome = (typeof MOCK_CHECKOUT_OUTCOMES)[number];
export const mockCheckoutCompleteSchema = z.object({ outcome: z.enum(MOCK_CHECKOUT_OUTCOMES) });

export const MOCK_PORTAL_ACTIONS = [
  "trocar_plano",
  "cancelar",
  "cancelar_agora",
  "reativar",
  "simular_falha",
  "pagar_pendente",
  "trocar_cartao",
  /** Viagem no tempo do modo simulado (não existem no Stripe): vencer a tolerância e o teste. */
  "avancar_tolerancia",
  "expirar_teste",
] as const;
export type MockPortalAction = (typeof MOCK_PORTAL_ACTIONS)[number];
export const mockPortalActionSchema = z.object({
  action: z.enum(MOCK_PORTAL_ACTIONS),
  planCode: z.string().trim().max(40).optional(),
  cycle: z.enum(BILLING_CYCLES).optional(),
});
export type MockPortalActionInput = z.input<typeof mockPortalActionSchema>;

// ---- DTOs --------------------------------------------------------------------------------------

export interface PlanDto {
  id: string;
  code: string;
  name: string;
  description: string;
  priceMonthlyCents: number;
  priceYearlyCents: number;
  limits: PlanLimits;
  features: PlanFeatures;
  active: boolean;
  sortOrder: number;
}

export interface AdminPlanDto extends PlanDto {
  stripePriceIds: StripePriceIds;
  subscriberCount: number;
}

export interface PaymentMethodSummary {
  type: "card" | "boleto" | "pix" | "outro";
  brand: string | null;
  last4: string | null;
}

export interface EntitlementsDto {
  plan: { id: string; code: string; name: string };
  status: SubscriptionStatus;
  cycle: BillingCycle;
  limits: PlanLimits;
  features: PlanFeatures;
  access: {
    state: AccessState;
    readOnly: boolean;
    reason: ReadOnlyReason | null;
    message: string | null;
    trialDaysLeft: number | null;
    graceEndsAt: string | null;
  };
  trialEndsAt: string | null;
}

export type UsageDto = Record<LimitKey, number>;

export interface InvoiceDto {
  id: string;
  number: string | null;
  status: string;
  amountDueCents: number;
  amountPaidCents: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  hostedInvoiceUrl: string | null;
  pdfUrl: string | null;
  paymentMethodType: string | null;
  failureReason: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface BillingOverviewDto {
  mode: BillingMode;
  canManage: boolean;
  entitlements: EntitlementsDto;
  usage: UsageDto;
  subscription: {
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    paymentMethod: PaymentMethodSummary | null;
    /** Próxima cobrança estimada (centavos), ou null sem assinatura paga. */
    nextPaymentCents: number | null;
    nextPaymentAt: string | null;
    hasStripeSubscription: boolean;
    discount: DiscountInfo | null;
  };
  invoices: InvoiceDto[];
  plans: PlanDto[];
  /** Formas aceitas no checkout (o Pix recorrente não existe para empresas no Brasil). */
  paymentMethods: string[];
}

export interface RedirectDto {
  url: string;
}

export interface MockCheckoutSessionDto {
  id: string;
  kind: "checkout" | "portal";
  status: "aberta" | "concluida" | "expirada";
  workspaceId: string;
  workspaceName: string;
  plan: PlanDto | null;
  cycle: BillingCycle;
  amountCents: number | null;
  coupon: { code: string; percentOff: number | null; amountOffCents: number | null } | null;
  returnUrl: string;
  subscriptionStatus: SubscriptionStatus | null;
  currentPlanCode: string | null;
  cancelAtPeriodEnd: boolean;
  plans: PlanDto[];
}

// ---- Painel do dono ----------------------------------------------------------------------------

export interface AdminMeDto {
  isSuperAdmin: boolean;
  twoFactorEnabled: boolean;
  /** Passou pela confirmação com o código do aplicativo nesta sessão. */
  elevated: boolean;
  elevatedUntil: string | null;
}

export interface AdminOverviewDto {
  totals: {
    workspaces: number;
    active: number;
    trialing: number;
    pastDue: number;
    canceled: number;
    readOnly: number;
    suspended: number;
  };
  mrrCents: number;
  arrCents: number;
  newThisMonth: number;
  canceledThisMonth: number;
  churnRate: number;
  series: { month: string; mrrCents: number }[];
  byPlan: { code: string; name: string; count: number; mrrCents: number }[];
}

export interface AdminWorkspaceRowDto {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string | null;
  planCode: string;
  planName: string;
  status: SubscriptionStatus;
  cycle: BillingCycle;
  readOnly: boolean;
  suspended: boolean;
  trialEndsAt: string | null;
  mrrCents: number;
  members: number;
  adAccounts: number;
  createdAt: string;
}

export interface AdminWorkspaceDetailDto extends AdminWorkspaceRowDto {
  entitlements: EntitlementsDto;
  usage: UsageDto;
  suspendedReason: string | null;
  membersList: { userId: string; name: string; email: string; role: string; createdAt: string }[];
  connections: { id: string; name: string; status: string; adAccounts: number }[];
  metaErrors: { at: string; source: string; message: string }[];
  ai: { month: string; usedCredits: number; monthlyCredits: number; calls: number; costUsdMicros: number };
  invoices: InvoiceDto[];
  subscription: {
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    pastDueSince: string | null;
    discount: DiscountInfo | null;
  };
  impersonations: { id: string; adminName: string | null; startedAt: string; endedAt: string | null }[];
}

export interface CouponDto {
  id: string;
  code: string;
  name: string;
  percentOff: number | null;
  amountOffCents: number | null;
  duration: "once" | "repeating" | "forever";
  durationInMonths: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  active: boolean;
  stripeCouponId: string | null;
  createdAt: string;
}

export interface ImpersonationDto {
  id: string;
  workspaceId: string;
  workspaceName: string;
  startedAt: string;
  expiresAt: string;
}

export interface WorkspaceDeletionReportDto {
  id: string;
  workspaceId: string;
  workspaceName: string;
  deletedAt: string;
  rows: Record<string, number>;
  r2ObjectsDeleted: number;
  metaTokensRevoked: number;
  metaRevokeErrors: string[];
  usersDeleted: number;
  stripe: string;
}
